/**
 * Main company search integration file
 * Uses the 'company-search' Supabase Edge Function
 */
import { toast } from "@/hooks/use-toast";
import { CompanySearchResult } from "./company-search/types";
import { supabase } from "@/integrations/supabase/client";

// Define a type for the logo data returned by the function
interface LogoResult {
  brandId: string;
  name: string;
  icon: string;
  domain: string;
  c: string;
  cc: string; // This is a constant value in the example, but could be dynamic if needed
  // Add other relevant fields if needed, like domain, score, etc.
}

// Define the structure of the successful response from the utility function
export interface CompanySearchResponse {
  potentialCompanies: CompanySearchResult[];
  logos: LogoResult[];
}

/**
 * Search for Belgian companies by name or VAT number using the Supabase Edge Function 'company-search'
 * Returns all potential matches and associated logos.
 */
export const searchBelgianCompany = async (
  searchTerm: string
): Promise<CompanySearchResponse | null> => {
  // Check if the search term looks like a Belgian VAT number (BE prefix or just 10 digits)
  const cleanedSearchTerm = searchTerm.replace(/\\s/g, "");
  const isVatNumber = /^BE\\d{10}$/.test(cleanedSearchTerm);
  const isNumeric = /^\\d{10}$/.test(cleanedSearchTerm);
  const searchParam = isVatNumber || isNumeric ? 
    { companyVAT: cleanedSearchTerm.startsWith('BE') ? cleanedSearchTerm : `BE${cleanedSearchTerm}` } : 
    { companyName: searchTerm };

  try {
    //console.log(`Invoking company-search function with:`, searchParam);
    const { data, error } = await supabase.functions.invoke('company-search', {
      body: searchParam,
    });

    if (error) {
      console.error("Error invoking Supabase function 'company-search':", error);
      toast({
        title: "Company Search Error",
        description: `Failed to search company: ${error.message}`,
        variant: "destructive",
      });
      return null;
    }

    //console.log("Supabase function 'company-search' response:", data);

    // Process the response data
    const potentialCompanies: CompanySearchResult[] = (data?.potentialCompanies || []).map((company: any) => ({
      name: company.denomination || "N/A",
      vat_number: company.cbe_number_formatted || company.cbe_number,
      street: company.street,
      postal_code: company.post_code,
      city: company.city,
      // logo_url is handled separately via the logos array
    }));
    
    // Check if logo data exists and is an array before mapping
    let logos: LogoResult[] = [];
    if (data?.logo && Array.isArray(data.logo)) {
      logos = data.logo.map((logo: any) => {
        //console.log("Logo data:", `https://cdn.brandfetch.io/${encodeURIComponent(logo.domain)}/w/400/h/400?c=1ids7hCwAd3Eb4FWJFi`);
        
        return {
          brandId: logo.brandId,
          name: logo.name,
          icon: `https://cdn.brandfetch.io/${encodeURIComponent(logo.domain)}/w/400/h/400?c=1ids7hCwAd3Eb4FWJFi`,
          domain: logo.domain,
          c: logo.c,
          cc: '1ids7hCwAd3Eb4FWJFi'
        };
      });
    } else if (data?.logo && !Array.isArray(data.logo)) {
      // If logo exists but is not an array (likely an object), handle it as a single item
      const logo = data.logo;
      if (logo.domain) {
        //console.log("Single logo data:", `https://cdn.brandfetch.io/${encodeURIComponent(logo.domain)}/w/400/h/400?c=1ids7hCwAd3Eb4FWJFi`);
        
        logos = [{
          brandId: logo.brandId || 'unknown',
          name: logo.name || 'unknown',
          icon: `https://cdn.brandfetch.io/${encodeURIComponent(logo.domain)}/w/400/h/400?c=1ids7hCwAd3Eb4FWJFi`,
          domain: logo.domain,
          c: logo.c,
          cc: '1ids7hCwAd3Eb4FWJFi'
        }];
      }
    }

    if (potentialCompanies.length === 0 && logos.length === 0) {
      //console.log("No potential companies or logos found in the response.");
      toast({
        title: "Not Found",
        description: `No companies or logos found for "${searchTerm}".`,
      });
      return null; // Return null if nothing found
    }
    
    return { potentialCompanies, logos };

  } catch (error) {
    console.error("Unexpected error during company search:", error);
    toast({
      title: "Company Search Error",
      description: "An unexpected error occurred while searching for the company.",
      variant: "destructive",
    });
    return null;
  }
};

// Re-export types for convenience
export type { CompanySearchResult } from "./company-search/types";
// Keep LogoResult export if needed elsewhere, otherwise remove
// export type { LogoResult };
